/**
 * ConversationalAgentManager - المدير الرئيسي للمحادثات
 * يحلل الرسائل ويوجهها للـ Agent المناسب
 */

export class ConversationalAgentManager {
  constructor(geminiApiKey) {
    this.apiKey = geminiApiKey;
    // الموديلات الصحيحة المتاحة
    this.geminiUrls = [
      'https://generativelanguage.googleapis.com/v1/models/gemini-2.5-flash:generateContent',
      'https://generativelanguage.googleapis.com/v1beta/models/gemini-2.5-flash:generateContent',
      'https://generativelanguage.googleapis.com/v1/models/gemini-2.0-flash:generateContent',
      'https://generativelanguage.googleapis.com/v1beta/models/gemini-2.0-flash:generateContent',
      'https://generativelanguage.googleapis.com/v1/models/gemini-2.5-pro:generateContent',
      'https://generativelanguage.googleapis.com/v1beta/models/gemini-2.5-pro:generateContent'
    ];
    this.currentUrlIndex = 0;
    this.conversationHistory = new Map(); // {conversationId: [...messages]}
    this.conversationState = new Map(); // {conversationId: {scenario, sector, step, data}}
  }

  /**
   * معالجة رسالة المستخدم
   */
  async processMessage(userMessage, conversationId) {
    try {
      console.log('🔵 Processing message:', userMessage);
      
      // الحصول على السياق
      const history = this.conversationHistory.get(conversationId) || [];
      const state = this.conversationState.get(conversationId) || {
        scenario: null,
        sector: null,
        step: 'initial',
        data: {}
      };

      // إضافة رسالة المستخدم للتاريخ
      history.push({
        role: 'user',
        content: userMessage
      });

      console.log('🔵 Analyzing message...');
      // تحليل الرسالة وتحديد الإجراء
      const analysis = await this.analyzeMessage(userMessage, history, state);
      console.log('✅ Analysis:', analysis);
      
      // تحديث الحالة
      if (analysis.scenario) state.scenario = analysis.scenario;
      if (analysis.sector) state.sector = analysis.sector;
      if (analysis.step) state.step = analysis.step;
      if (analysis.data) state.data = { ...state.data, ...analysis.data };

      console.log('🔵 Generating response...');
      // توليد الرد المناسب
      const response = await this.generateResponse(analysis, state, history);
      console.log('✅ Response generated');

      // إضافة رد البوت للتاريخ
      history.push({
        role: 'assistant',
        content: response.message
      });

      // حفظ التاريخ والحالة
      this.conversationHistory.set(conversationId, history);
      this.conversationState.set(conversationId, state);

      return {
        success: true,
        response: response,
        state: state
      };

    } catch (error) {
      console.error('❌ Error in processMessage:', error);
      console.error('Full error:', error.message, error.stack);
      
      // Fallback: استخدام كشف الكلمات المفتاحية - بدون تفاصيل
      const scenario = this.detectScenarioFromKeywords(userMessage);
      const sector = this.detectSectorFromKeywords(userMessage);
      
      return {
        success: true,
        response: {
          message: this.getFallbackResponse(userMessage, scenario, sector),
          scenario: null, // لا تعرض السيناريو في الـ fallback
          scenarioDetails: null,
          trackingNumber: null,
          estimatedTime: null,
          actions: null,
          nextSteps: null
        },
        state: {
          scenario: scenario,
          sector: sector,
          step: 'fallback'
        }
      };
    }
  }

  /**
   * رد احتياطي عند فشل Gemini - محادثة تفاعلية
   */
  getFallbackResponse(userMessage, scenario, sector) {
    const lower = userMessage.toLowerCase();
    
    // بيانات وهمية للمستخدم
    const userData = {
      name: 'أحمد محمد العتيبي',
      id: '10XXXXXXXX',
      phone: '0555XXXXXX',
      email: 'ahmed.m***@gmail.com'
    };
    
    // كشف الإيميل
    const hasEmail = lower.includes('@');
    
    // كشف التأكيد الأول (وصلت الرسالة)
    const isFirstConfirmation = (lower.includes('نعم') || lower.includes('وصل') || lower.includes('لقيت') || lower.includes('اي')) && !lower.includes('تمام') && !lower.includes('خلاص');
    
    // كشف النجاح النهائي
    const isSuccess = lower.includes('تمام') || lower.includes('خلاص') || lower.includes('اشتغل') || lower.includes('دخلت') || lower.includes('سويت');
    
    // كلمة مرور - محادثة تدريجية
    if (lower.includes('كلمة') || lower.includes('مرور') || lower.includes('دخول') || lower.includes('حساب') || lower.includes('مايفتح')) {
      return `وعليكم السلام ورحمة الله أخي ${userData.name.split(' ')[0]} 👋

أهلاً وسهلاً! لا تقلق، أنا هنا لمساعدتك وحل المشكلة بسرعة 😊

🔍 **تحليل سريع لحالتك:**
✅ الحساب: نشط وسليم
✅ المحاولات: 4 محاولات فاشلة
⚠️ تنبيه: محاولة واحدة متبقية قبل القفل المؤقت

**الحل الأمثل:** إعادة تعيين كلمة المرور 🔐

📋 **معلومات حسابك:**
- الاسم: ${userData.name}
- رقم الهوية: ${userData.id}
- الجوال المسجل: ${userData.phone}
- البريد: ${userData.email}

**هل هذه معلوماتك الصحيحة؟**`;
    }
    
    // إذا قال الجوال مختلف
    if (lower.includes('لا') || lower.includes('مختلف') || lower.includes('تغير') || lower.includes('غلط')) {
      return `ممتاز! شكراً على التوضيح 📱

🔄 **تحديث مهم:**
لاحظت أن رقم جوالك تغير. هذا يفسر سبب المشكلة!

**عندي لك خيارين:**

**الخيار الأول (الأسرع):** ✅
- أرسل رابط إعادة التعيين على بريدك الإلكتروني
- الوقت: دقيقتين فقط

**الخيار الثاني:**
- تحديث رقم الجوال أولاً ثم إرسال الرابط
- الوقت: 5 دقائق

**أيهما تفضل؟**`;
    }
    
    // إذا اختار الخيار الأول
    if (lower.includes('الاول') || lower.includes('الأول') || lower.includes('ايميل') || lower.includes('بريد')) {
      return `تمام! خيار ممتاز 🚀

⏳ **جاري إرسال رابط إعادة التعيين...**

📧 البريد: ${userData.email}
🔐 نوع الطلب: إعادة تعيين كلمة المرور
⏱️ صلاحية الرابط: 15 دقيقة

✅ **تم الإرسال بنجاح!**

📬 **تفقد بريدك الإلكتروني الآن:**
- ابحث عن رسالة من: noreply@mol.gov.sa
- الموضوع: "إعادة تعيين كلمة المرور - وزارة العمل"
- إذا لم تجدها في البريد الوارد، تفقد مجلد الرسائل غير المرغوبة (Spam)

**هل وصلتك الرسالة؟**`;
    }
    
    // إذا أكد المعلومات
    if (lower.includes('صح') || lower.includes('صحيح') || (lower.includes('اي') && !hasEmail && !lower.includes('وصل'))) {
      return `ممتاز! شكراً على التأكيد 📱

⏳ **جاري إرسال رابط إعادة التعيين...**

📧 البريد: ${userData.email}
🔐 نوع الطلب: إعادة تعيين كلمة المرور
⏱️ صلاحية الرابط: 15 دقيقة

✅ **تم الإرسال بنجاح!**

📬 **تفقد بريدك الإلكتروني الآن:**
- ابحث عن رسالة من: noreply@mol.gov.sa
- الموضوع: "إعادة تعيين كلمة المرور - وزارة العمل"
- إذا لم تجدها في البريد الوارد، تفقد مجلد الرسائل غير المرغوبة (Spam)

**هل وصلتك الرسالة؟**`;
    }
    
    // إذا قال وصلت الرسالة
    if (isFirstConfirmation) {
      return `رائع! 🎉

👇 **الآن اتبع هذه الخطوات:**

**1️⃣ افتح الرابط من البريد**
- اضغط على الزر الأزرق "إعادة تعيين كلمة المرور"

**2️⃣ أدخل كلمة مرور جديدة**
⚠️ **مهم جداً** - كلمة المرور يجب أن تحتوي على:
- 8 أحرف على الأقل
- حرف كبير (A-Z)
- حرف صغير (a-z)
- رقم (0-9)
- رمز خاص (@#$%&*)

**مثال:** Ahmed@2025

**3️⃣ أعد كتابة كلمة المرور للتأكيد**

**4️⃣ اضغط "حفظ"**

**هل أنت جاهز للبدء؟**`;
    }
    
    // إذا قال تمام/خلاص
    if (isSuccess) {
      return `الحمدلله! ممتاز أخي ${userData.name.split(' ')[0]} 🎉

✅ **تم حل المشكلة بنجاح!**

📊 **ملخص الخدمة:**
⏱️ وقت الحل: دقيقتين و 47 ثانية
✅ الحالة: تم الحل بالكامل
🎯 النوع: آلي بالكامل

**هل تحتاج مساعدة في شيء آخر؟**`;
    }
    
    // راتب
    if (lower.includes('راتب')) {
      return `وعليكم السلام! 👋

فهمت، عندك مشكلة في الراتب.

سأتخذ الإجراءات التالية:
✅ إرسال استفسار رسمي لجهة العمل
✅ فتح ملف متابعة
✅ تحديثات دورية

⏱️ الوقت المتوقع: 24-48 ساعة

سيتم التواصل معك قريباً.`;
    }
    
    // عنف
    if (lower.includes('عنف') || lower.includes('ضرب')) {
      return `🚨 أختي الكريمة، أنا آسف جداً لما تمرين به.

سلامتك أولويتنا.

تم إنشاء بلاغ سري فوراً.
🔴 الأولوية: عاجل جداً

سأحولك لأخصائية حماية متخصصة خلال 5-10 دقائق.

📞 أرقام الطوارئ:
- خط الحماية: 1919 (24/7)
- الطوارئ: 911

أنتِ لست وحدك. نحن معك.`;
    }
    
    // عام
    return `وعليكم السلام! 👋

شكراً لتواصلك معنا.

تم استلام رسالتك وسيتم معالجتها قريباً.

⏱️ الوقت المتوقع: ${this.getEstimatedTime(scenario)}

يمكنك متابعة حالتك برقم التتبع أعلاه.`;
  }

  /**
   * تحليل رسالة المستخدم
   */
  async analyzeMessage(userMessage, history, currentState) {
    const prompt = this.buildAnalysisPrompt(userMessage, history, currentState);
    const response = await this.callGemini(prompt);
    
    try {
      // استخراج JSON من الرد
      const jsonMatch = response.match(/\{[\s\S]*\}/);
      if (jsonMatch) {
        return JSON.parse(jsonMatch[0]);
      }
    } catch (e) {
      console.error('Error parsing analysis:', e);
    }

    // Fallback
    return {
      scenario: this.detectScenarioFromKeywords(userMessage),
      sector: this.detectSectorFromKeywords(userMessage),
      step: 'continue',
      intent: 'general',
      data: {}
    };
  }

  /**
   * بناء prompt التحليل
   */
  buildAnalysisPrompt(userMessage, history, state) {
    const historyText = history.slice(-4).map(h => 
      `${h.role === 'user' ? 'المستخدم' : 'البوت'}: ${h.content}`
    ).join('\n');

    return `أنت محلل ذكي لنظام سبق. حلل الرسالة التالية وحدد:

المحادثة السابقة:
${historyText}

الحالة الحالية:
- السيناريو: ${state.scenario || 'غير محدد'}
- القطاع: ${state.sector || 'غير محدد'}
- الخطوة: ${state.step}

رسالة المستخدم الجديدة: "${userMessage}"

حدد:
1. السيناريو المناسب:
   - آلي_بالكامل: مشاكل بسيطة (كلمة مرور، استفسارات)
   - نصف_آلي: مشاكل متوسطة (راتب متأخر، شكاوى عمل)
   - يدوي: مشاكل معقدة (عنف، قضايا قانونية)

2. القطاع:
   - العمل: رواتب، عقود، نقل خدمات
   - التنمية_الاجتماعية: عنف أسري، حماية
   - الخدمة_المدنية: موظفين حكوميين

3. نية المستخدم (intent):
   - greeting: تحية
   - complaint: شكوى
   - question: سؤال
   - confirmation: تأكيد
   - provide_info: تقديم معلومات

4. الخطوة التالية (step):
   - initial: بداية المحادثة
   - gathering_info: جمع معلومات
   - processing: معالجة
   - resolved: تم الحل
   - escalated: تم التصعيد

5. بيانات إضافية (data): أي معلومات مهمة من الرسالة

أجب بصيغة JSON فقط:
{
  "scenario": "آلي_بالكامل أو نصف_آلي أو يدوي",
  "sector": "العمل أو التنمية_الاجتماعية أو الخدمة_المدنية",
  "intent": "greeting أو complaint أو ...",
  "step": "الخطوة",
  "urgency": "منخفض أو متوسط أو عالي أو طارئ",
  "data": {}
}`;
  }

  /**
   * توليد الرد المناسب
   */
  async generateResponse(analysis, state, history) {
    let prompt = '';

    // اختيار الـ prompt حسب السيناريو
    if (state.scenario === 'آلي_بالكامل') {
      prompt = this.buildAutomatedPrompt(analysis, state, history);
    } else if (state.scenario === 'نصف_آلي') {
      prompt = this.buildSemiAutomatedPrompt(analysis, state, history);
    } else if (state.scenario === 'يدوي') {
      prompt = this.buildManualPrompt(analysis, state, history);
    } else {
      prompt = this.buildInitialPrompt(analysis, state, history);
    }

    const response = await this.callGemini(prompt);

    // فقط أعطي التفاصيل الكاملة إذا تم حل المشكلة فعلياً
    const lastUserMessage = history.length > 0 ? history[history.length - 1].content.toLowerCase() : '';
    const userConfirmedSuccess = lastUserMessage.includes('دخلت') || 
                                  lastUserMessage.includes('اشتغل') || 
                                  lastUserMessage.includes('تمام سويت') ||
                                  lastUserMessage.includes('خلاص');
    
    const isResolved = (state.step === 'resolved' || userConfirmedSuccess) && 
                       (response.includes('تم حل المشكلة') || 
                        response.includes('تم الحل بنجاح') ||
                        response.includes('الحمدلله'));
    
    return {
      message: response,
      scenario: isResolved ? state.scenario : null,
      scenarioDetails: isResolved ? this.getScenarioDetails(state.scenario) : null,
      trackingNumber: isResolved ? (state.data.trackingNumber || this.generateTrackingNumber()) : null,
      estimatedTime: isResolved ? this.getEstimatedTime(state.scenario) : null,
      actions: isResolved ? this.getActions(state) : null,
      nextSteps: isResolved ? this.getNextSteps(state) : null
    };
  }

  /**
   * Prompt للسيناريو الآلي
   */
  buildAutomatedPrompt(analysis, state, history) {
    const historyText = history.slice(-4).map(h => 
      `${h.role === 'user' ? 'المستخدم' : 'البوت'}: ${h.content}`
    ).join('\n');

    return `أنت مساعد ذكي ودود في نظام سبق - السيناريو الآلي بالكامل.

المحادثة السابقة:
${historyText}

الحالة:
- الخطوة: ${state.step}
- القطاع: ${state.sector}

بيانات المستخدم المتاحة:
- الاسم: أحمد محمد العتيبي
- رقم الهوية: 10XXXXXXXX
- الجوال: 0555XXXXXX
- البريد: ahmed.m***@gmail.com

تعليمات مهمة جداً:
1. **استخدم بيانات المستخدم مباشرة** - لا تسأل عن الإيميل أو الاسم
2. **كن مباشراً وسريعاً** - حل المشكلة بأقل خطوات
3. **لا تسأل أسئلة كثيرة** - اتخذ الإجراء مباشرة
4. كن ودوداً ومطمئناً
5. استخدم لغة بسيطة
6. أضف emoji واحد أو اثنين فقط
7. كن مختصراً (2-4 أسطر)

**سيناريو كلمة المرور - خطوة بخطوة:**

1. **أول رد**: أخبره أنك سترسل الرابط على بريده
   "وعليكم السلام أخي أحمد! 👋 سأرسل لك رابط إعادة التعيين على بريدك ahmed.m***@gmail.com الآن. هل وصلك؟"

2. **إذا قال "وصل"**: أعطه التعليمات
   "رائع! 🎉 الآن افتح الرابط وأدخل كلمة مرور جديدة.
   ⚠️ يجب أن تحتوي على: 8 أحرف، حرف كبير، حرف صغير، رقم، رمز خاص.
   هل تمكنت من الدخول؟"

3. **إذا قال "دخلت"**: احتفل بالنجاح
   "الحمدلله! ممتاز أخي أحمد 🎉 تم حل المشكلة بنجاح!"

**لا تفعل:**
❌ لا تسأل عن البريد (أنت تعرفه)
❌ لا تنهي المحادثة قبل أن يدخل فعلياً
❌ لا تقول "تم الحل" إلا إذا قال المستخدم "دخلت"

الرد بالعربية مباشرة بدون مقدمات.`;
  }

  /**
   * Prompt للسيناريو النصف آلي
   */
  buildSemiAutomatedPrompt(analysis, state, history) {
    const historyText = history.slice(-4).map(h => 
      `${h.role === 'user' ? 'المستخدم' : 'البوت'}: ${h.content}`
    ).join('\n');

    return `أنت مساعد ذكي متعاطف في نظام سبق - السيناريو النصف آلي.

المحادثة السابقة:
${historyText}

الحالة:
- الخطوة: ${state.step}
- القطاع: ${state.sector}
- الإلحاح: ${analysis.urgency || 'متوسط'}

بيانات المستخدم المتاحة:
- الاسم: أحمد محمد العتيبي
- رقم الهوية: 10XXXXXXXX
- الجوال: 0555XXXXXX
- البريد: ahmed.m***@gmail.com
- جهة العمل: شركة النور للتجارة

تعليمات مهمة:
1. **كن متعاطفاً ومطمئناً** - أظهر تفهمك للموقف
2. **استخدم البيانات المتاحة** - لا تسأل عن معلومات موجودة
3. **اسأل سؤال واحد فقط** - لا تطلب 4-5 معلومات مرة واحدة
4. **كن مختصراً** - 3-4 أسطر فقط
5. **لا تعطي رقم بلاغ إلا في النهاية** - بعد جمع المعلومات

**سيناريو راتب متأخر - خطوة بخطوة:**

1. **أول رد**: اطمئنه واسأل سؤال واحد
   "وعليكم السلام أخي أحمد! 👋 أتفهم وضعك تماماً، وسأساعدك في حل هذه المشكلة. متى كان آخر راتب استلمته؟"

2. **بعد الإجابة**: اطلب مستندات
   "تمام، لتسريع المعالجة، يرجى رفع المستندات التالية:
   📎 كشف حساب بنكي (آخر 3 أشهر)
   📎 عقد العمل أو خطاب التعيين
   
   يمكنك رفع الملفات بالضغط على زر 📎 أسفل الشاشة."

3. **بعد رفع الملفات**: أكد الاستلام وأخبره بالإجراء
   "ممتاز! ✅ تم استلام المستندات.
   سأقوم بإرسال استفسار رسمي لشركة النور مع المرفقات.
   ⏱️ الوقت المتوقع: 24-48 ساعة.
   سأبلغك بأي تحديث على جوالك 0555XXXXXX."

4. **إذا شكر**: أعطه الملخص النهائي
   "العفو أخي أحمد! تم تسجيل طلبك بنجاح ✅"

**لا تفعل:**
❌ لا تسأل 4-5 أسئلة مرة واحدة
❌ لا تعطي رقم بلاغ في أول رد
❌ لا تكتب فقرات طويلة

الرد بالعربية مباشرة بدون مقدمات.`;
  }

  /**
   * Prompt للسيناريو اليدوي
   */
  buildManualPrompt(analysis, state, history) {
    const historyText = history.slice(-4).map(h => 
      `${h.role === 'user' ? 'المستخدم' : 'البوت'}: ${h.content}`
    ).join('\n');

    return `أنت مساعد ذكي متعاطف في نظام سبق - السيناريو اليدوي (حالات عاجلة).

المحادثة السابقة:
${historyText}

الحالة:
- الخطوة: ${state.step}
- القطاع: ${state.sector}
- الإلحاح: ${analysis.urgency || 'عالي'}

بيانات المستخدم المتاحة:
- الاسم: فاطمة أحمد السالم
- رقم الهوية: 10XXXXXXXX
- الجوال: 0555XXXXXX
- البريد: fatima.a***@gmail.com

⚠️ **تعليمات حرجة - اتبعها بالضبط:**

**هذه حالة طارئة - عنف أسري!**

1. **السلامة أولاً** - اسأل فوراً: "هل أنتِ في مكان آمن الآن؟"
2. **لا تطلب مستندات** - هذه حالة طوارئ، ليس وقت المستندات!
3. **حوّل فوراً** - قل "سأحولك لأخصائية خلال 5 دقائق"
4. **أعطِ أرقام طوارئ** - خط الحماية 1919، الطوارئ 911
5. **كن مختصراً جداً** - 3-4 أسطر فقط، لا فقرات طويلة
6. **أكد السرية** - "البلاغ سري تماماً"
7. **تعاطف عميق** - "أنتِ لست وحدك ❤️"

**سيناريو عنف أسري - خطوة بخطوة:**

1. **أول رد**: اطمئن على السلامة فوراً
   "🚨 أختي الكريمة، أنا آسف جداً لما تمرين به.
   سلامتك وسلامة أطفالك أولويتنا القصوى.
   
   هل أنتِ في مكان آمن الآن؟"

2. **بعد التأكد من السلامة**: أنشئ بلاغ سري وحوّل لمختصة
   "تم إنشاء بلاغ سري فوراً 🔴
   
   سأحولك لأخصائية حماية متخصصة خلال 5 دقائق.
   
   📞 أرقام الطوارئ:
   - خط الحماية: 1919 (24/7)
   - الطوارئ: 911
   
   أنتِ لست وحدك. نحن معك. ❤️"

3. **إذا طلبت مساعدة إضافية**: أكد الدعم المستمر
   "سنبقى معك خطوة بخطوة.
   الأخصائية ستتصل بك خلال دقائق.
   البلاغ سري تماماً ولن يعلم به أحد."

**❌ ممنوع تماماً:**
❌ لا تطلب مستندات أو هوية أو إثباتات
❌ لا تقول "5-7 أيام" - قل "5 دقائق"
❌ لا تكتب فقرات طويلة
❌ لا تعطي رقم قضية في أول رد
❌ لا تطلب تفاصيل العنف
❌ لا تؤخر التحويل للمختص

الرد بالعربية مباشرة بدون مقدمات.`;
  }

  /**
   * Prompt للبداية
   */
  buildInitialPrompt(analysis, state, history) {
    return `أنت مساعد ذكي ودود في نظام سبق.

المستخدم قال: "${history[history.length - 1].content}"

تعليمات:
1. رحب بشكل طبيعي
2. اسأله كيف يمكنك مساعدته
3. كن مختصراً (سطرين فقط)
4. استخدم emoji واحد فقط

الرد بالعربية مباشرة بدون مقدمات.`;
  }

  /**
   * استدعاء Gemini API
   */
  async callGemini(prompt) {
    console.log('🔵 Testing API Key:', this.apiKey.substring(0, 10) + '...');
    
    // جرب كل الـ URLs واحد ورا الثاني
    for (let i = 0; i < this.geminiUrls.length; i++) {
      const url = this.geminiUrls[i];
      try {
        console.log(`🔵 Trying [${i + 1}/${this.geminiUrls.length}]:`, url.split('models/')[1]);
        
        const requestBody = {
          contents: [{ 
            parts: [{ text: prompt }] 
          }],
          generationConfig: {
            temperature: 0.9,
            topK: 1,
            topP: 1,
            maxOutputTokens: 2048,
          },
          safetySettings: [
            {
              category: "HARM_CATEGORY_HARASSMENT",
              threshold: "BLOCK_NONE"
            },
            {
              category: "HARM_CATEGORY_HATE_SPEECH",
              threshold: "BLOCK_NONE"
            },
            {
              category: "HARM_CATEGORY_SEXUALLY_EXPLICIT",
              threshold: "BLOCK_NONE"
            },
            {
              category: "HARM_CATEGORY_DANGEROUS_CONTENT",
              threshold: "BLOCK_NONE"
            }
          ]
        };
        
        console.log('📤 Request body:', JSON.stringify(requestBody).substring(0, 100) + '...');
        
        const response = await fetch(`${url}?key=${this.apiKey}`, {
          method: 'POST',
          headers: { 
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(requestBody)
        });

        console.log(`📥 Response status [${i + 1}]:`, response.status);

        const data = await response.json();
        console.log(`📥 Response data [${i + 1}]:`, JSON.stringify(data).substring(0, 200));

        if (!response.ok) {
          console.warn(`⚠️ URL ${i + 1} failed (${response.status}):`, data);
          continue;
        }
        
        if (data.candidates && data.candidates[0] && data.candidates[0].content) {
          console.log(`✅ SUCCESS with URL ${i + 1}!`);
          this.currentUrlIndex = i;
          return data.candidates[0].content.parts[0].text;
        }
        
        if (data.error) {
          console.warn(`⚠️ URL ${i + 1} error:`, data.error);
          continue;
        }
        
      } catch (error) {
        console.warn(`⚠️ URL ${i + 1} exception:`, error.message);
        continue;
      }
    }
    
    console.error('❌ All Gemini URLs failed!');
    throw new Error('All Gemini API endpoints failed');
  }

  /**
   * كشف السيناريو من الكلمات المفتاحية
   */
  detectScenarioFromKeywords(text) {
    const lower = text.toLowerCase();
    
    // يدوي
    if (lower.match(/عنف|ضرب|تهديد|خطر|طارئ|اعتداء|قانون|محامي|قضية|اعتراض/)) {
      return 'يدوي';
    }
    
    // نصف آلي
    if (lower.match(/راتب|متأخر|شكوى|مشكلة|تأخير|تقصير/)) {
      return 'نصف_آلي';
    }
    
    // آلي
    if (lower.match(/كلمة مرور|كلمة السر|دخول|حساب|تسجيل|استفسار/)) {
      return 'آلي_بالكامل';
    }
    
    return null;
  }

  /**
   * كشف القطاع من الكلمات المفتاحية
   */
  detectSectorFromKeywords(text) {
    const lower = text.toLowerCase();
    
    if (lower.match(/عنف|أسري|زوج|حماية|إيذاء/)) {
      return 'التنمية_الاجتماعية';
    }
    
    if (lower.match(/راتب|عمل|عامل|وافد|شركة|مؤسسة|نقل خدمات/)) {
      return 'العمل';
    }
    
    if (lower.match(/موظف حكومي|خدمة مدنية|إجازة|ترقية/)) {
      return 'الخدمة_المدنية';
    }
    
    return 'العمل'; // default
  }

  /**
   * توليد رقم تتبع
   */
  generateTrackingNumber() {
    const timestamp = Date.now().toString().slice(-8);
    const random = Math.floor(Math.random() * 1000);
    return `SBQ-${timestamp}-${random}`;
  }

  /**
   * الحصول على تفاصيل السيناريو
   */
  getScenarioDetails(scenario) {
    const details = {
      'آلي_بالكامل': {
        color: '#10b981',
        icon: '🟢',
        name: 'السيناريو الأول - آلي بالكامل',
        description: 'حل تلقائي فوري بدون تدخل بشري'
      },
      'نصف_آلي': {
        color: '#f59e0b',
        icon: '🟡',
        name: 'السيناريو الثاني - نصف آلي',
        description: 'معالجة ذكية مع مراجعة بشرية'
      },
      'يدوي': {
        color: '#ef4444',
        icon: '🔴',
        name: 'السيناريو الثالث - يدوي',
        description: 'تدخل بشري متخصص فوري'
      }
    };
    
    return details[scenario] || null;
  }

  /**
   * الحصول على الوقت المتوقع
   */
  getEstimatedTime(scenario) {
    const times = {
      'آلي_بالكامل': '5-15 دقيقة',
      'نصف_آلي': '24-48 ساعة',
      'يدوي': '5-7 أيام عمل'
    };
    
    return times[scenario] || 'سيتم تحديده';
  }

  /**
   * الحصول على الإجراءات
   */
  getActions(state) {
    if (!state.scenario) return [];
    
    const actions = {
      'آلي_بالكامل': [
        '✅ تم تحليل الطلب تلقائياً',
        '✅ تم تنفيذ الإجراءات المطلوبة',
        '✅ تم إرسال التأكيد'
      ],
      'نصف_آلي': [
        '✅ تم تحليل الشكوى بالذكاء الاصطناعي',
        '🔄 جاري اتخاذ الإجراءات اللازمة',
        '⏳ في انتظار المراجعة النهائية'
      ],
      'يدوي': [
        '✅ تم تصنيف الحالة',
        '✅ تم تحويلها لمختص متخصص',
        '🚨 تم تفعيل بروتوكول المتابعة'
      ]
    };
    
    return actions[state.scenario] || [];
  }

  /**
   * الحصول على الخطوات التالية
   */
  getNextSteps(state) {
    if (!state.scenario) return [];
    
    const steps = {
      'آلي_بالكامل': [
        'تحقق من بريدك الإلكتروني',
        'ستصلك رسالة نصية بالتأكيد',
        'يمكنك المتابعة برقم التتبع'
      ],
      'نصف_آلي': [
        'سيتم التواصل معك خلال 24 ساعة',
        'يمكنك متابعة الحالة برقم التتبع',
        'سنرسل لك تحديثات دورية'
      ],
      'يدوي': [
        'سيتواصل معك مختص خلال المدة المحددة',
        'احتفظ برقم التذكرة للمتابعة',
        'يمكنك الاستفسار في أي وقت'
      ]
    };
    
    return steps[state.scenario] || [];
  }

  /**
   * مسح المحادثة
   */
  clearConversation(conversationId) {
    this.conversationHistory.delete(conversationId);
    this.conversationState.delete(conversationId);
  }
}
